/** Shared DOM builders used by the Visual + Debug + Sounds sections inside SettingsModal.
 *  Lifted from the old SettingsPanel.ts so multiple sections can share the same
 *  slider / toggle / divider styling. Pure functions — they append to the parent and
 *  return whatever the section needs to bind events / refresh values. */

export interface SliderHandles {
  row: HTMLElement;
  slider: HTMLInputElement;
  value: HTMLElement;
}

export function addDivider(parent: HTMLElement): void {
  const d = document.createElement('div');
  d.className = 'settings-divider';
  parent.appendChild(d);
}

export function addSectionTitle(parent: HTMLElement, text: string): void {
  const t = document.createElement('div');
  t.className = 'settings-section-title';
  t.textContent = text;
  parent.appendChild(t);
}

export function addNote(parent: HTMLElement, text: string): void {
  const n = document.createElement('div');
  n.className = 'settings-note';
  n.textContent = text;
  parent.appendChild(n);
}

/** Slider with a value-readout label. `format` converts the numeric value to display
 *  text; `onInput` fires while dragging (use to mutate the underlying settings object). */
export function addSlider(
  parent: HTMLElement,
  label: string,
  min: number,
  max: number,
  step: number,
  initial: number,
  format: (v: number) => string,
  onInput: (v: number) => void,
  opts: { disabled?: boolean } = {},
): SliderHandles {
  const row = document.createElement('div');
  row.className = 'settings-row';

  const lab = document.createElement('label');
  lab.className = 'settings-label';
  lab.textContent = label;

  const value = document.createElement('span');
  value.className = 'settings-value';
  value.textContent = format(initial);

  const slider = document.createElement('input');
  slider.type = 'range';
  slider.className = 'settings-slider';
  slider.min = String(min);
  slider.max = String(max);
  slider.step = String(step);
  slider.value = String(initial);
  if (opts.disabled) slider.disabled = true;

  slider.addEventListener('input', () => {
    const v = Number.parseFloat(slider.value);
    value.textContent = format(v);
    onInput(v);
  });

  const labelRow = document.createElement('div');
  labelRow.className = 'settings-label-row';
  labelRow.appendChild(lab);
  labelRow.appendChild(value);
  row.appendChild(labelRow);
  row.appendChild(slider);
  parent.appendChild(row);
  return { row, slider, value };
}

export interface ToggleHandles {
  row: HTMLElement;
  checkbox: HTMLInputElement;
}

export function addToggle(
  parent: HTMLElement,
  label: string,
  initial: boolean,
  onChange: (checked: boolean) => void,
  opts: { disabled?: boolean } = {},
): ToggleHandles {
  const row = document.createElement('div');
  row.className = 'settings-row settings-row-toggle';
  if (opts.disabled) row.classList.add('settings-row-disabled');

  const lab = document.createElement('label');
  lab.className = 'settings-label';
  lab.textContent = label;

  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  checkbox.className = 'settings-checkbox';
  checkbox.checked = initial;
  if (opts.disabled) checkbox.disabled = true;
  checkbox.addEventListener('change', () => onChange(checkbox.checked));

  row.appendChild(lab);
  row.appendChild(checkbox);
  parent.appendChild(row);
  return { row, checkbox };
}
